import type { SessionResponse } from '@transfers-system/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiError } from '@/lib/api/client.js';
import { can, type PermissionSubject } from '@/components/shared/permissions.js';

/** The Unified Access web app this platform trusts (unified-access.md section 3). */
export function uaPortalUrl(): string {
  return (import.meta.env['VITE_UA_PORTAL_URL'] as string | undefined) ?? 'http://localhost:5200';
}

/**
 * unified-access.md section 3, step 5 and ui-contract.md section 6: sends the
 * browser to Unified Access with the current absolute page as `returnTo`, so
 * the employee lands back on the same page after signing in again.
 */
export function redirectToSignIn(): void {
  const returnTo = encodeURIComponent(globalThis.location.href);
  globalThis.location.href = `${uaPortalUrl()}/login?returnTo=${returnTo}`;
}

export const SESSION_QUERY_KEY = ['session'] as const;

/**
 * Cookie session, so there is no client-held token to branch on (unlike the
 * old JWT `authStore`) - `GET /auth/me` is the source of truth. A 401 means
 * "no session"; every other failure is a real error the caller should show.
 */
export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => apiRequest<SessionResponse>('/auth/me'),
    retry: false,
    staleTime: 60_000,
  });
}

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/** ui-contract.md section 1: `can()` gates every nav item, menu item and action. */
export function useCan(service: string): boolean {
  const { data } = useSession();
  return can(data as PermissionSubject | undefined, service);
}

/**
 * unified-access.md section 3.6: signing out in a platform signs out of
 * Unified Access too. This clears the local `trf_session` cookie first, then
 * sends the browser to Unified Access' navigable `/logout` page, which ends
 * the central session and lands on its login screen. Every other platform's
 * session then ends on its next check with Unified Access.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      globalThis.location.href = `${uaPortalUrl()}/logout`;
    },
  });
}
